import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Viewer } from '@gatecheckpro/contracts';
import { ApiRefusal, type StoredSession } from '@gatecheckpro/api-client';
import { api } from './api.ts';

/**
 * The signed-in viewer.
 *
 * The workspace renders itself entirely from `/auth/viewer`, so what the
 * navigation offers and what the API will honour come from one source. A menu
 * item the server would refuse is a product lie, and this is the mechanism that
 * makes one impossible rather than merely unlikely (kickoff Section 7).
 */
export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'anonymous' }
  | { readonly status: 'authenticated'; readonly viewer: Viewer };

interface SessionContextValue {
  readonly state: SessionState;
  /**
   * Declared as function-valued properties rather than methods on purpose.
   * Surfaces destructure these off the context and pass them to handlers, and a
   * method shorthand would carry a `this` binding that destructuring silently
   * drops.
   */
  readonly reload: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  // Resolved lazily rather than defaulting to `loading` and immediately
  // correcting it: with no stored credential there is nothing to wait for, and a
  // synchronous correction inside an effect is a cascading render.
  const [state, setState] = useState<SessionState>(() =>
    api.session() === null ? { status: 'anonymous' } : { status: 'loading' },
  );

  /**
   * Re-reads the viewer.
   *
   * Every path through this awaits before touching state, so nothing here can
   * fire a synchronous render cascade from inside an effect. The caller decides
   * whether there is a credential worth exchanging; a missing one would simply
   * come back 401 and land in the catch below.
   */
  const reload = useCallback(async () => {
    try {
      const viewer = await api.request<Viewer>('/api/v1/auth/viewer');
      setState({ status: 'authenticated', viewer });
    } catch (error) {
      // An expired or revoked session is an ordinary outcome, not an error to
      // surface. Anything else is worth showing, but not at the cost of
      // trapping the user on a blank screen.
      if (error instanceof ApiRefusal && error.status === 401) {
        api.setSession(null);
      }
      setState({ status: 'anonymous' });
    }
  }, []);

  /**
   * Reacts to the client dropping a session — an expired credential, or a failed
   * refresh. Defined outside the effect so it reads as what it is: a listener
   * invoked later, never during the render that subscribes it.
   */
  const onClientSessionLost = useCallback((session: StoredSession | null) => {
    if (session === null) setState({ status: 'anonymous' });
  }, []);

  useEffect(() => {
    // Only when there is a credential worth exchanging; the anonymous case was
    // already settled by the lazy initial state.
    //
    // The rule below traces `reload` into its catch, where a 401 clears the
    // stored session and that in turn notifies `onClientSessionLost`. Every step
    // of that chain happens after an await, so no state is set during the render
    // that runs this effect — but the analysis cannot see the await boundary
    // through the listener indirection.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (api.session() !== null) void reload();
    return api.onSessionChange(onClientSessionLost);
  }, [reload, onClientSessionLost]);

  const signOut = useCallback(async () => {
    try {
      await api.request('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // The server may already consider this session gone. Either way the
      // client must stop believing it holds one.
    }
    api.setSession(null);
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo(() => ({ state, reload, signOut }), [state, reload, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession used outside SessionProvider');
  return value;
}

/** The authenticated viewer, or throws. For use below a route guard. */
export function useViewer(): Viewer {
  const { state } = useSession();
  if (state.status !== 'authenticated') throw new Error('useViewer used while not authenticated');
  return state.viewer;
}
